import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,  
    CircularProgress, 
    Tooltip, 
    IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByFlt } from 'state/api';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FilterButton from 'components/FilterButton';
import CrewListDialog from 'components/CrewListDialog';
import CardList from 'components/CardList';
import FlightFilterDialog from './FlightFilterDialog';


const DuplicateInvoicedFlights = () => {
    useAuthCheck();
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode); 
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const canView = hasRight(permissionMap, "DuplicateInvoicedFlights", "view", false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);
    const [flights, setFlights] = useState([]);
    
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isInvoiced: 'ALL' });
    const defaultFilters = {
        isInvoiced: 'ALL',
    };
        
    
    useEffect(() => {
        const handleFlights = async () => {            
            try {           
                if (!startDate || !endDate) return [];

                setError(null); 
                setLoading(true);

                const url = '/inflightsales/duplicateinvoicedflights';
                const payload = { startDate, endDate }
                
                const response = await api.post(url, payload);
                const result = response.data;  
                
                setFlights(result);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        handleFlights();      
    }, [startDate, endDate]); 
    
    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setCrewList([]);
    };

    const filteredFlights = useMemo(() => {
        if (!Array.isArray(flights)) {
            return []; 
        }
    
        if (!filters) return flights; 
    
        return flights.filter((flight) => {
            const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
            const minEtd = filters.minEtd;
            const maxEtd = filters.maxEtd;

            if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
            if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

            return Object.entries(filters).every(([key, value]) => {
                if (!value) return true; // If no value, don't filter on this key
                
                if (key === 'minEtd' || key === 'maxEtd') return true;

                // Handle date filtering
                if (key === 'flightDate') {
                    const formattedFlightDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                    const formattedFilterDate = value ? format(new Date(value), 'yyyy-MM-dd') : null;
                    return formattedFlightDate === formattedFilterDate;
                }
    
                // Handle flight number filtering
                if (key === 'flightNo') {
                    return flight[key] === Number(value); 
                }
    
                // Handle isInvoiced filtering
                if (key === 'isInvoiced') {
                    if (value === 'ALL') return true; // Include all flights
                    if (value === 'INVOICED') return flight[key] === true; // Only invoiced flights
                    if (value === 'NOT_INVOICED') return flight[key] === false; // Only non-invoiced flights
                }
    
                // For other fields, continue normal comparison
                return flight[key] === value;
            });
        });
    }, [timeMode, flights, filters]);      

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleViewClick = useCallback(async (param) => {
        try {      
            const flightDate = param.flightDate;
            const flightDesc = param.flightDesc;
            const dep = param.dep;
            
            const {flightDetails, legMembers, jumpSeaters} = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
        } 
    }, []);
    
    const columns = useMemo(() => [
        {
            field: "no",
            headerName: "No",
            filterable: false,      
            sortable: false,
            renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
            ...(() => {
                return isMobile ? { width: 60 } : { flex: 1 };
            })(),
        },
        {
            field: "crewCode",
            headerName: "Code",
            ...(() => {
                return isMobile ? { width: 80 } : { flex: 1 };
            })(),
        },
        {
            field: "crewName",
            headerName: "InputBy",
            ...(() => {
                return isMobile ? { width: 280 } : { flex: 4 };
            })(),
        },
        {
            field: "createdAt",
            headerName: "CreatedAt",
            ...(() => {
                return isMobile ? { width: 180 } : { flex: 2.5 };
            })(),
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "HH:mm:ss dd/MM/yy")}`;
            },
        },
        {
            field: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase",
            headerName: "Date",
            pinned: 'left',
            valueFormatter: (value) => {
                if (value == null) {
                  return '';
                }
                return `${format(value, "dd/MM/yy")}`;
            },
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 2 };
            })(),
        },        
        {
            field: "flightNo",
            headerName: "Flt",
            ...(() => {
                return isMobile ? { width: 85 } : { flex: 1.5 };
            })(),
        },        
        {
            field: "sector",
            headerName: "Sector",
            ...(() => {
                return isMobile ? { width: 120 } : { flex: 2 };
            })(),
        },             
        {
            field: timeMode === 'UTC' ? "etd" : "etdLocalBase",
            headerName: "Etd",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        },
        {
            field: timeMode === 'UTC' ? "eta" : "etaLocalBase",
            headerName: "Eta",
            ...(() => {
                return isMobile ? { width: 100 } : { flex: 1.5 };
            })(),
        }, 
    ], [timeMode, isMobile]);
    
    
    const actionColumn = useMemo(() => {
        const activeCount = [canView].filter(Boolean).length;
        if (activeCount === 0) return [];

        const baseWidth = 60 + activeCount * 25;

        return [{
            field: "action",
            headerName: "Action",
            width: baseWidth,
            filterable: false,      
            sortable: false,
            renderCell: (params) => (
                <FlexCenter
                    gap={1}
                    sx={{ width: '100%', height: '100%' }}
                >         
                    {canView && (
                        <Tooltip title="View">
                            <IconButton
                                aria-label="View"
                                sx={{
                                    color: theme.palette.primary.dark,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: theme.palette.green.deep,
                                    }
                                }}
                                onClick={() => {
                                    handleViewClick(params.row)
                                }}
                            >
                                <VisibilityIcon />
                            </IconButton>
                        </Tooltip>
                    )}   
                </FlexCenter>
            ),
        }]
    }, [canView, theme, handleViewClick]); 
    
    const columnsToShow = [...columns, ...actionColumn];
   
    const {
        acTypeOptions,
        flightRegOptions,
        // sectorTypeOptions,
        // departureTypeOptions,
        // arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);   
    

    return (
        <Box className="main-container-box">            
            <FlexBetween>
                <Header 
                    title="Duplicate Flights" 
                    subtitle={`${loading ? 'Loading...' : 'List of Flights'}`}
                /> 
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>              
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={filteredFlights}
                                primaryField="flightNo"
                                secondaryField="sector"
                                fields={[
                                    { label: "Input By", key: "crewName" },
                                    { label: "Code", key: "crewCode" },
                                    { label: "Created At", key: "createdAt" },
                                    { label: "Date", key: timeMode === 'UTC' ? "flightDate" : "flightDateLocalBase" },
                                    { label: "Flight No", key: "flightNo" },
                                    { label: "Sector", key: "sector" },
                                    { label: "ACType", key: "acType" },
                                    { label: "Reg", key: "flightReg" },
                                    { label: "Etd", key: timeMode === 'UTC' ? "etd" : "etdLocalBase" },
                                    { label: "Eta", key: timeMode === 'UTC' ? "eta" : "etaLocalBase" },
                                    { label: "Atd", key: timeMode === 'UTC' ? "atd" : "atdLocalBase" },
                                    { label: "Ata", key: timeMode === 'UTC' ? "ata" : "ataLocalBase" },
                                ]}
                                isActiveField={null}
                                canView={canView}
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const f = filteredFlights.find(x => x._id === id);
                                    if (f) handleViewClick(f);
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={filteredFlights || []}
                                columns={columnsToShow}
                                loading={loading}
                                props={{
                                    initialState: {
                                        pagination: { paginationModel: { pageSize: 100 } },
                                    },
                                }}
                                sx={{
                                    "& .MuiDataGrid-row.invoiced-false": {
                                        backgroundColor: `${theme.palette.primary.light} !important`,
                                    },
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {openCrewListDialog &&
                <CrewListDialog
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default DuplicateInvoicedFlights;
